'use client'

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { authService } from '../api/auth.service'
import { StorageEnum } from '../enum/storage.enum'
import {
  JwtOrganization,
  JwtPayload,
  getOrganizationsFromPayload,
  getStoredJwtPayload,
  persistLoginSession,
} from '@/lib/jwt'

export interface AuthContextType {
  user: Record<string, unknown> | null
  organizations: JwtOrganization[]
  selectedOrganizationId: string | null
  sessionVersion: number
  login: (email: string, password: string) => Promise<{ access_token: string; authId?: string }>
  logout: () => Promise<void>
  loading: boolean
  completeLoginSession: (
    token: string,
    payload: JwtPayload,
    org?: JwtOrganization,
    authId?: string
  ) => Record<string, unknown>
  switchOrganization: (organizationId: string) => void
  setUser: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>
}

const AuthContext = createContext<AuthContextType | null>(null)

function loadOrganizations(): JwtOrganization[] {
  const payload = getStoredJwtPayload()
  return payload ? getOrganizationsFromPayload(payload) : []
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Record<string, unknown> | null>(null)
  const [organizations, setOrganizations] = useState<JwtOrganization[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null)
  const [sessionVersion, setSessionVersion] = useState(0)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    const storedUser = localStorage.getItem(StorageEnum.userData)
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
    setOrganizations(loadOrganizations())
    setSelectedOrganizationId(localStorage.getItem(StorageEnum.selectedOrganizationId))
    setLoading(false)
  }, [])

  const login = async (email: string, secret: string) => {
    const resp = await authService.login({ email, secret })
    return resp.data
  }

  const completeLoginSession = (
    token: string,
    payload: JwtPayload,
    org?: JwtOrganization,
    authId?: string
  ) => {
    const userData = persistLoginSession(token, payload, org, authId)
    setUser(userData)
    setOrganizations(getOrganizationsFromPayload(payload))
    setSelectedOrganizationId(org?.organizationId ?? null)
    setSessionVersion((v) => v + 1)
    return userData
  }

  const switchOrganization = useCallback((organizationId: string) => {
    if (organizationId === selectedOrganizationId) return

    const token = localStorage.getItem(StorageEnum.userToken)
    const payload = getStoredJwtPayload()
    if (!token || !payload) return

    const org = getOrganizationsFromPayload(payload).find(
      (o) => o.organizationId === organizationId
    )
    if (!org) return

    const authId = localStorage.getItem(StorageEnum.fluidAuthId) ?? undefined
    const userData = persistLoginSession(token, payload, org, authId)
    setUser(userData)
    setSelectedOrganizationId(organizationId)
    setSessionVersion((v) => v + 1)
    router.refresh()
  }, [selectedOrganizationId, router])

  const logout = async () => {
    try {
      await fetch('/api/logout', {
        method: 'GET',
        credentials: 'include',
      })
      setUser(null)
      setOrganizations([])
      setSelectedOrganizationId(null)
      setSessionVersion(0)
      localStorage.clear()
      document.cookie = `${StorageEnum.userToken}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
      router.push('/auth')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        organizations,
        selectedOrganizationId,
        sessionVersion,
        login,
        logout,
        loading,
        completeLoginSession,
        switchOrganization,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
